import type { SkillNeedTemplate } from '@lmc/ts-common'
import { Modal } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { Divider } from 'antd'
import type { FC } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { useSkillTemplateDelete } from '../../../../hooks/useSkillTemplates'

type DeleteSkillTemplateModalProps = {
  isModalVisible: boolean
  onClose: () => void
  template: SkillNeedTemplate | undefined
  onRefetch: () => void
  preferredPeopleNominative?: string
}

export const DeleteSkillTemplateModal: FC<DeleteSkillTemplateModalProps> = ({
  isModalVisible,
  onClose,
  template,
  onRefetch,
  preferredPeopleNominative
}) => {
  const { t } = useTranslation()

  const handleClose = () => {
    onClose()
  }

  const { deleteSkillTemplate, isDeleting } = useSkillTemplateDelete(() => {
    handleClose()
    onRefetch()
  })

  const onConfirm = async () => {
    if (!template?.id) return

    try {
      await deleteSkillTemplate(template.id)
    } catch {
      // error handled by the hook
    }
  }

  return (
    <StyledModal
      localisation={{
        title: t('skillsAndNeeds.deleteSkillModalTitle'),
        confirmText: t('skillsAndNeeds.yesDelete'),
        cancelText: t('general.buttons.cancel')
      }}
      onConfirm={onConfirm}
      onCancel={handleClose}
      isModalVisible={isModalVisible}
      closeButton
      closeOnConfirm={false}
      noPadding
      confirmButtonProps={{ isLoading: isDeleting, variant: 'danger' }}
    >
      <Divider style={{ margin: '0' }} />
      <StyledContent>
        <Text level={1} weight="medium">
          {t('skillsAndNeeds.byDeleting')}
        </Text>
        <StyledWarningBlock>
          <Text level={1}>
            {t('skillsAndNeeds.skillsRemoved', {
              variable: preferredPeopleNominative
            })}
          </Text>
          <StyledList>
            <li>
              <Text level={1}>
                {t('skillsAndNeeds.deleteSkillProfilesItem')}
              </Text>
            </li>
          </StyledList>
        </StyledWarningBlock>
      </StyledContent>
    </StyledModal>
  )
}

const StyledContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px 0;
  border-bottom: 1px solid ${({ theme }) => theme.color.neutral.color8};
`

const StyledWarningBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  color: ${({ theme }) => theme.color.neutral.color3};
  margin-top: -4px;
`

const StyledList = styled.ul`
  margin: 0;
  padding-left: 20px;
`

const StyledModal = styled(Modal)`
  min-width: 600px;

  .ant-modal-content {
    padding-top: 20px !important;
    padding-left: 24px !important;
    padding-right: 24px !important;
  }

  .ant-modal-header {
    padding-bottom: 4px !important;
  }

  .ant-modal-title {
    font-size: 22px !important;
    font-weight: 500 !important;
    text-transform: none !important;
    color: ${({ theme }) => theme.color.neutral.color3} !important;
  }

  .ant-modal-footer {
    margin-top: 0 !important;
    padding-top: 16px !important;
    padding-bottom: 16px !important;
  }
`

export default DeleteSkillTemplateModal
